import { AssetOrientation, AttachmentPoints } from './attachmentPoints';
import { Particle, ParticleWithModelUrl } from './particle';
import { hash } from '../../hash';

/**
 *  Data about attachments from services
 *
 *  TODO: move this to a ParticleWithAttachmentData kinda type (see ./particle file)
 * */
export interface RawParticleData extends Particle {
    document: AttachmentPoints;
}

export type Position = {
    x: number;
    y: number;
    z: number;
};

export type Rotation = {
    w: number;
    x: number;
    y: number;
    z: number;
};

/** We deserialise particle data in multiple passes. Each pass returns some extension of this data in a tree structure. */
export interface ParticleData extends Particle {
    attachmentTag: string;
    position: Position;
    orientation: Rotation;
    originDelta: AssetOrientation;
}

/** first pass, grab all the raw data from the attachment documents converted to a convenient tree structure */
export type ParticleTreeNodeWithSlots = ParticleData & {
    slots: ParticleAttachmentSlots[];
};

export interface ParticleAttachmentSlots {
    name: string;
    isOptional: boolean;
    attached: ParticleTreeNodeWithSlots[];
    applicableTags: string[];
}

/** second pass, parse the tree into required and optional attachments */
export type ParticleTreeNodeWithAttachments = ParticleData &
    ParticleWithModelUrl & {
        attachments: {
            required: ParticleAttachment[];
            options: { [tag: string]: ParticleAttachment[] };
        };
    };

export interface ParticleAttachment {
    name: string;
    attached: ParticleTreeNodeWithAttachments[];
}

export const isParticleAttachment = (
    node: ParticleTreeNode | ParticleAttachment,
): node is ParticleAttachment => {
    return (node as ParticleAttachment).name !== undefined;
};

/** third pass, given the users' current options RE: attachment choices, merge required and selected options
 *  (options object is left un-altered to display choices in the UI)
 */
export type ParticleTreeNode = ParticleData & {
    modelUrl: string;
    attachments: {
        active: ParticleTreeNode[];
        options: { [tag: string]: (ParticleTreeNode | ParticleAttachment)[] };
    };
};

export type TagAttachmentOptions = {
    tag: string;
    options: string[];
    selected: string;
};

export const logNode = (treeNode: ParticleTreeNode) => {
    // Particle tree logging bypasses logging framework as it's
    // controlled by a dedicated flag
    console.log('[Renderer] New Furniture received', treeNode);
    const logRecursive = (treeNode: ParticleTreeNode) => {
        const hasAttached = treeNode.attachments.active.length > 0;
        const attachedNodes = hasAttached
            ? `, ${treeNode.attachments.active.length} attached nodes:`
            : '';
        const logString = `${treeNode.attachmentTag}: ${treeNode.id}${attachedNodes}`;
        hasAttached ? console.group(logString) : console.log(logString);
        if (treeNode.attachments.active.length) {
            treeNode.attachments.active.flatMap(attached => logRecursive(attached));
        }
        hasAttached && console.groupEnd();
    };
    logRecursive(treeNode);
};

/**
 * Generate a unique hash for a given particle tree node, using the unique particle id and the position / rotation.
 * This means a specific instance of an attachment can be compared against another tree directly.
 * @param treeNode the particle node to read from
 * @returns string SHA-256 hash
 */
export const transformHash = async (treeNode: ParticleTreeNode) => {
    const hashString =
        treeNode.id + JSON.stringify(treeNode.position) + JSON.stringify(treeNode.orientation);

    return hash(hashString);
};

export function getIds(tree: ParticleTreeNode): Array<string> {
    return [tree.id, ...tree.attachments.active.flatMap(getIds)];
}
